import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useAlert } from '../contexts/AlertContext';
import { Message } from '../types/chat';
import InternetApplyModal from '../components/InternetApplyModal';
import './ChatDetail.css';
import iconBack from '../assets/icon-back.svg';
import iconExit from '../assets/icon-exit.svg';
import iconPlus from '../assets/images/icon_plus.svg';
import iconCall from '../assets/images/icon_call.svg';
import iconImg from '../assets/images/icon_img.svg';
import iconFolder from '../assets/images/icon_folder.svg';
import iconCamera from '../assets/images/icon_camera.svg';
import iconIdCard from '../assets/images/icon_id-card.svg';

const PlusIcon = () => (
  <img src={iconPlus} alt="+" width="28" height="28" />
);

const CloseIcon = () => (
  <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
    <path d="M21 7L7 21M7 7L21 21" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

interface ChatRoom {
  id: string;
  customer_id: string;
  counselor_id: string | null;
  status: string;
  product_type?: string;
}

interface ChatDetailProps {
  roomId: string;
  onBack?: () => void;
}

const ChatDetail: React.FC<ChatDetailProps> = ({ roomId, onBack }) => {
  const { customer } = useAuth();
  const { showAlert } = useAlert();
  const [inputMessage, setInputMessage] = useState('');
  const [showAttachModal, setShowAttachModal] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [chatRoom, setChatRoom] = useState<ChatRoom | null>(null);
  const [uploading, setUploading] = useState(false);
  const [toast, setToast] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const [phoneNumber, setPhoneNumber] = useState('1877-1847'); // 기본값
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [showApplicationDetail, setShowApplicationDetail] = useState(false);
  const [applicationDetail, setApplicationDetail] = useState<any>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  // Supabase에서 전화번호 설정값 가져오기
  useEffect(() => {
    const fetchPhoneNumber = async () => {
      try {
        const { data, error } = await supabase
          .from('app_settings')
          .select('setting_value')
          .eq('setting_key', 'customer_service_phone')
          .eq('is_active', true)
          .single();

        if (error) {
          console.error('전화번호 조회 오류:', error);
          return;
        }

        if (data?.setting_value) {
          setPhoneNumber(data.setting_value);
        }
      } catch (error) {
        console.error('전화번호 가져오기 실패:', error);
      }
    };

    fetchPhoneNumber();
  }, []);

  // 웹/앱 환경 구분
  const isWebView = () => {
    return !!(window.ReactNativeWebView || (window as any).flutter_inappwebview || (window as any).FlutterFilePicker);
  };

  // Flutter 네이티브 파일 피커 사용 가능 여부
  const hasFlutterFilePicker = () => {
    return !!(window as any).FlutterFilePicker;
  };

  // Flutter 네이티브 파일 피커 호출
  const requestFlutterFilePicker = (type: 'gallery' | 'camera' | 'file') => {
    if ((window as any).FlutterFilePicker) {
      (window as any).FlutterFilePicker.postMessage(type);
      setShowAttachModal(false);
    }
  };

  // Flutter에서 파일 선택 결과 처리
  const handleFlutterFilePicked = async (fileData: { name: string; type: string; data: string }) => {
    if (!roomId || !customer) return;

    setUploading(true);

    try {
      // base64를 Blob으로 변환
      const byteCharacters = atob(fileData.data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: fileData.type });
      const file = new File([blob], fileData.name, { type: fileData.type });

      // 파일 크기 제한 (10MB)
      if (file.size > 10 * 1024 * 1024) {
        alert('파일 크기는 10MB 이하여야 합니다.');
        return;
      }

      // 파일명 생성 (중복 방지)
      const fileExt = file.name.split('.').pop();
      const fileName = `${roomId}/${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;

      // Supabase Storage에 업로드
      const { error: uploadError } = await supabase.storage
        .from('chat-files')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;

      // 공개 URL 가져오기
      const { data: { publicUrl } } = supabase.storage
        .from('chat-files')
        .getPublicUrl(fileName);

      // 파일 타입 확인
      const isImage = file.type.startsWith('image/');
      const messageType = isImage ? 'image' : 'file';

      // 메시지 생성
      const { data: newMsg, error: msgError } = await supabase.from('messages').insert([{
        room_id: roomId,
        sender_id: customer.id,
        message_type: messageType,
        content: file.name,
        file_url: publicUrl,
        file_name: file.name,
        is_read: false
      }]).select().single();

      if (msgError) throw msgError;

      if (newMsg) {
        setMessages(prev => [...prev, newMsg]);
      }

      await supabase.from('chat_rooms')
        .update({ last_message_at: new Date().toISOString() })
        .eq('id', roomId);

    } catch (error) {
      console.error('파일 업로드 오류:', error);
      alert('파일 업로드에 실패했습니다.');
    } finally {
      setUploading(false);
    }
  };

  // Flutter 파일 피커 콜백 등록
  useEffect(() => {
    (window as any).onFlutterFilePicked = handleFlutterFilePicked;
    (window as any).onFlutterFilePickerError = (error: string) => {
      console.error('Flutter 파일 피커 오류:', error);
      setUploading(false);
    };

    return () => {
      delete (window as any).onFlutterFilePicked;
      delete (window as any).onFlutterFilePickerError;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId, customer]);

  // 전화 버튼 클릭 핸들러
  const handlePhoneCall = () => {
    if (isWebView()) {
      // 앱 환경: 전화 앱으로 연결
      window.location.href = `tel:${phoneNumber}`;
    } else {
      // 웹 환경: 모달로 전화번호 표시
      showAlert(`상담 전화번호\n\n${phoneNumber}`);
    }

    setShowAttachModal(false);
  };

  // 채팅방 나가기 핸들러 (고객용)
  const handleExitChat = async () => {
    if (!roomId || !customer) return;

    const confirmed = window.confirm('채팅방을 나가시겠습니까?\n나가기 후에는 채팅 내역을 확인할 수 없습니다.');
    if (!confirmed) return;

    try {
      // 채팅방 나가기 시간 기록 (관리자는 여전히 볼 수 있음)
      const { error: roomError } = await supabase
        .from('chat_rooms')
        .update({
          customer_exited_at: new Date().toISOString()
        })
        .eq('id', roomId)
        .eq('customer_id', customer.id); // 본인의 채팅방만 나가기 가능

      if (roomError) throw roomError;

      // 채팅 목록으로 이동
      if (onBack) onBack();
    } catch (error) {
      console.error('채팅방 나가기 오류:', error);
      showAlert('채팅방 나가기에 실패했습니다.');
    }
  };

  useEffect(() => {
    if (roomId && customer) {
      fetchChatRoom();
      fetchMessages();

      const channel = supabase
        .channel('room_' + roomId)
        .on('postgres_changes', {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: 'room_id=eq.' + roomId
        }, async (payload: any) => {
          console.log('🔔 New message received:', payload);
          const newMessage = payload.new as Message;

          if (newMessage.sender_id !== customer.id) {
            console.log('✅ Adding message from counselor to UI');
            setMessages(prev => [...prev, newMessage]);

            // 실시간 메시지도 즉시 읽음 처리 (채팅방에 있을 때)
            if (!newMessage.is_read) {
              await supabase
                .from('messages')
                .update({ is_read: true })
                .eq('id', newMessage.id);
            }
          }
        })
        .subscribe((status) => {
          console.log('📡 Realtime status:', status);
        });

      return () => {
        supabase.removeChannel(channel);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId, customer]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(''), 2000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  // 신청서 상세보기
  const handleViewApplicationDetail = async () => {
    try {
      // 신청서 정보 조회
      const { data: appData, error: appError } = await supabase
        .from('internet_applications')
        .select('*')
        .eq('room_id', roomId)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (appError) throw appError;

      // 채팅방 정보 (상품 정보) 조회
      const { data: roomData } = await supabase
        .from('chat_rooms')
        .select('carrier, internet_name, tv_name, monthly_price, cash_gift')
        .eq('id', roomId)
        .single();

      // 통신사 코드를 한글로 변환
      const carrierMap: Record<string, string> = {
        'SKT': 'SK브로드밴드',
        'sk': 'SK브로드밴드',
        'KT': 'KT',
        'kt': 'KT',
        'LG': 'LG유플러스',
        'lg': 'LG유플러스',
      };

      setApplicationDetail({
        ...appData,
        product_carrier: roomData ? (carrierMap[roomData.carrier] || roomData.carrier) : null,
        internet_name: roomData?.internet_name,
        tv_name: roomData?.tv_name,
        monthly_price: roomData?.monthly_price,
        cash_gift: roomData?.cash_gift,
      });
      setShowApplicationDetail(true);
    } catch (error) {
      console.error('신청서 조회 오류:', error);
      showAlert('신청서를 불러올 수 없습니다.');
    }
  };

  const fetchChatRoom = async () => {
    if (!roomId) return;
    try {
      const { data } = await supabase
        .from('chat_rooms')
        .select('*')
        .eq('id', roomId)
        .single();
      if (data) setChatRoom(data as ChatRoom);
    } catch (error) {
      console.error('Error fetching chat room:', error);
    }
  };

  const fetchMessages = async () => {
    if (!roomId || !customer) return;

    setLoading(true);

    try {
      const { data, error } = await supabase
        .from('messages')
        .select('*')
        .eq('room_id', roomId)
        .order('created_at', { ascending: true });

      if (error) {
        console.error('Error fetching messages:', error);
      }

      if (data) {
        setMessages(data);

        // 채팅방 입장 시 안 읽은 메시지 읽음 처리 (본인이 보낸 메시지 제외)
        const unreadMessages = data.filter(
          msg => !msg.is_read && msg.sender_id !== customer.id
        );

        if (unreadMessages.length > 0) {
          const unreadMessageIds = unreadMessages.map(msg => msg.id);

          const { error: updateError } = await supabase
            .from('messages')
            .update({ is_read: true })
            .in('id', unreadMessageIds);

          if (updateError) {
            console.error('Error marking messages as read:', updateError);
          }
        }
      } else {
        setMessages([]);
      }
    } catch (error) {
      console.error('Exception in fetchMessages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || !roomId || !customer) return;
    const content = inputMessage.trim();
    setInputMessage('');
    try {
      const { data: newMsg, error } = await supabase.from('messages').insert([{
        room_id: roomId,
        sender_id: customer.id,
        message_type: 'text',
        content: content,
        is_read: false
      }]).select().single();

      if (error) throw error;

      // 메시지를 즉시 화면에 추가
      if (newMsg) {
        setMessages(prev => [...prev, newMsg]);
      }

      // chat_rooms의 last_message_at 업데이트
      await supabase.from('chat_rooms')
        .update({ last_message_at: new Date().toISOString() })
        .eq('id', roomId);
    } catch (error) {
      console.error('Error:', error);
      setInputMessage(content);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') handleSendMessage();
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !roomId || !customer) return;

    // 파일 크기 제한 (10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('파일 크기는 10MB 이하여야 합니다.');
      return;
    }

    setUploading(true);
    setShowAttachModal(false);

    try {
      // 파일명 생성 (중복 방지)
      const fileExt = file.name.split('.').pop();
      const fileName = `${roomId}/${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;

      // Supabase Storage에 업로드
      const { error: uploadError } = await supabase.storage
        .from('chat-files')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;

      // 공개 URL 가져오기
      const { data: { publicUrl } } = supabase.storage
        .from('chat-files')
        .getPublicUrl(fileName);

      // 파일 타입 확인
      const isImage = file.type.startsWith('image/');
      const messageType = isImage ? 'image' : 'file';

      // 메시지 생성
      const { data: newMsg, error: msgError } = await supabase.from('messages').insert([{
        room_id: roomId,
        sender_id: customer.id,
        message_type: messageType,
        content: file.name,  // 파일명만 저장
        file_url: publicUrl,  // 전체 URL은 file_url에 저장
        file_name: file.name,
        is_read: false
      }]).select().single();

      if (msgError) throw msgError;

      // 메시지를 즉시 화면에 추가
      if (newMsg) {
        setMessages(prev => [...prev, newMsg]);
      }

      // chat_rooms의 last_message_at 업데이트
      await supabase.from('chat_rooms')
        .update({ last_message_at: new Date().toISOString() })
        .eq('id', roomId);

    } catch (error) {
      console.error('파일 업로드 오류:', error);
      alert('파일 업로드에 실패했습니다.');
    } finally {
      setUploading(false);
      if (imageInputRef.current) {
        imageInputRef.current.value = '';
      }
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      if (cameraInputRef.current) {
        cameraInputRef.current.value = '';
      }
    }
  };

  return (
    <div className="chat-detail-container">
      <div className="chat-detail-header">
        <div className="chat-detail-header-wrapper">
          <img src={iconBack} alt="뒤로가기" className="header-icon" onClick={onBack} />
          <h1 className="chat-detail-title">상담원 채팅</h1>
          <img src={iconExit} alt="나가기" className="header-icon" onClick={handleExitChat} />
        </div>
      </div>
      <div className="chat-detail-content">
        <div className="chat-notice">
          <p className="notice-title">채팅시 주의사항</p>
          <p className="notice-content">답변까지 최대 10분정도 소요될 수 있습니다.</p>
        </div>
        <div className="chat-messages-wrapper">
          {loading ? (
            <div className="chat-loading">메시지를 불러오는 중...</div>
          ) : (
            <div className="chat-messages">
              {messages.map((message) => {
                console.log('Message sender_id:', message.sender_id, 'Content:', message.content.substring(0, 30));
                const isSystemMessage = message.sender_id === '00000000-0000-0000-0000-000000000000';
                const isUserMessage = message.sender_id === customer?.id;

                // 시스템 메시지는 별도로 렌더링 (UUID로 된 것만)
                if (isSystemMessage) {
                  // application_card 타입이면 카드 형태로 렌더링
                  if (message.message_type === 'application_card') {
                    let cardData;
                    try {
                      cardData = JSON.parse(message.content);
                    } catch {
                      cardData = { title: '신청서' };
                    }

                    return (
                      <div key={message.id} className="message-wrapper bot-message">
                        <div className="message-avatar"></div>
                        <div className="message-content-wrapper">
                          <p className="message-id">상담원</p>
                          <div className="application-card">
                            <p className="application-card-title">{cardData.title || '인터넷 가입 신청서'}</p>
                            <p className="application-card-desc">고객님의 가입신청서가<br/>도착했습니다.</p>
                            <p className="application-card-notice">상세보기로 확인 가능합니다.</p>
                            <button
                              className="application-card-btn"
                              onClick={handleViewApplicationDetail}
                            >
                              신청서 상세보기
                            </button>
                          </div>
                          <span className="message-time">
                            {new Date(message.created_at).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div key={message.id} className="system-message-wrapper">
                      <div className="system-message">
                        {message.content}
                      </div>
                    </div>
                  );
                }

                return (<div key={message.id} className={'message-wrapper ' + (isUserMessage ? 'user-message' : 'bot-message')}>
                  {!isUserMessage && <div className="message-avatar"></div>}
                  <div className="message-content-wrapper">
                    {!isUserMessage && <p className="message-id">상담원</p>}
                    <div className={'message-bubble ' + (isUserMessage ? 'user' : 'bot')}>
                      {message.message_type === 'image' ? (
                        <div className="message-image-wrapper">
                          <img
                            src={message.file_url || message.content}
                            alt="업로드된 이미지"
                            className="message-image"
                            onClick={() => {
                              const imageUrl = message.file_url || message.content;
                              setSelectedImage(imageUrl);
                            }}
                            onError={(e) => {
                              console.error('이미지 로딩 실패:', message.file_url || message.content);
                              console.error('전체 URL:', e.currentTarget.src);
                              e.currentTarget.style.display = 'none';
                              const parent = e.currentTarget.parentElement;
                              if (parent) {
                                const errorMsg = document.createElement('p');
                                errorMsg.textContent = '이미지를 불러올 수 없습니다';
                                errorMsg.style.color = '#999';
                                errorMsg.style.fontSize = '12px';
                                parent.appendChild(errorMsg);
                              }
                            }}
                          />
                          {message.file_name && (
                            <p className="message-file-name">{message.file_name}</p>
                          )}
                        </div>
                      ) : message.message_type === 'pdf' || message.message_type === 'file' ? (
                        <div className="message-file-wrapper">
                          <div
                            className="message-file-link"
                            onClick={() => {
                              const fileUrl = message.file_url || message.content;
                              const fileName = message.file_name || 'download';
                              if ((window as any).FlutterFileDownloader) {
                                // 앱: Flutter로 다운로드 요청
                                (window as any).FlutterFileDownloader.postMessage(JSON.stringify({ url: fileUrl, fileName }));
                              } else {
                                // 웹: 새 탭으로 열기
                                window.open(fileUrl, '_blank');
                              }
                            }}
                            style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
                          >
                            {message.message_type === 'pdf' ? (
                              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.89 22 5.99 22H18C19.1 22 20 21.1 20 20V8L14 2Z" fill="#E74C3C" opacity="0.3"/>
                                <path d="M14 2V8H20" stroke="#E74C3C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                <text x="7" y="17" fill="#E74C3C" fontSize="6" fontWeight="bold">PDF</text>
                              </svg>
                            ) : (
                              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2Z" fill="currentColor" opacity="0.3"/>
                                <path d="M14 2V8H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            )}
                            <div className="file-info">
                              <p className="file-name">{message.file_name || '파일'}</p>
                              {message.file_size && (
                                <p className="file-size">{(message.file_size / 1024).toFixed(1)} KB</p>
                              )}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <p className="message-text">{message.content}</p>
                      )}
                    </div>
                  </div>
                </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>
        <div className={'chat-input-container ' + (showAttachModal ? 'modal-open' : '')}>
          <div className="chat-input-area">
            <div
              className="attach-button"
              onClick={() => chatRoom?.counselor_id && setShowAttachModal(!showAttachModal)}
              style={{ opacity: chatRoom?.counselor_id ? 1 : 0.5, cursor: chatRoom?.counselor_id ? 'pointer' : 'not-allowed' }}
            >
              {showAttachModal ? <CloseIcon /> : <PlusIcon />}
            </div>
            <div className="input-wrapper">
              <input
                type="text"
                placeholder={chatRoom?.counselor_id ? "메세지를 입력해 주세요." : "상담원 배정 대기 중..."}
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                className="message-input"
                disabled={!chatRoom?.counselor_id}
              />
              <button
                className="send-button"
                onClick={handleSendMessage}
                disabled={!chatRoom?.counselor_id}
              >
                전송
              </button>
            </div>
          </div>
          {showAttachModal && (
            <div className="attach-modal">
              <div className="attach-options">
                <button className="attach-option" onClick={handlePhoneCall}>
                  <img src={iconCall} alt="전화" className="attach-icon" />
                  <span>전화</span>
                </button>
                <button
                  className="attach-option"
                  onClick={() => hasFlutterFilePicker() ? requestFlutterFilePicker('gallery') : imageInputRef.current?.click()}
                  disabled={uploading}
                >
                  <img src={iconImg} alt="이미지" className="attach-icon" />
                  <span>{uploading ? '업로드 중...' : '이미지'}</span>
                </button>
                <button
                  className="attach-option"
                  onClick={() => hasFlutterFilePicker() ? requestFlutterFilePicker('file') : fileInputRef.current?.click()}
                  disabled={uploading}
                >
                  <img src={iconFolder} alt="파일" className="attach-icon" />
                  <span>{uploading ? '업로드 중...' : '파일'}</span>
                </button>
                <button
                  className="attach-option"
                  onClick={() => hasFlutterFilePicker() ? requestFlutterFilePicker('camera') : cameraInputRef.current?.click()}
                  disabled={(!isWebView() && !hasFlutterFilePicker()) || uploading}
                >
                  <img src={iconCamera} alt="카메라" className="attach-icon" />
                  <span>{uploading ? '업로드 중...' : '카메라'}</span>
                </button>
{(chatRoom?.product_type === 'internet' || chatRoom?.product_type === 'tv') && (
                <button className="attach-option" onClick={() => {
                  setShowAttachModal(false);
                  setShowApplyModal(true);
                }}>
                  <img src={iconIdCard} alt="신청하기" className="attach-icon" />
                  <span>신청하기</span>
                </button>
                )}
              </div>
            </div>
          )}
          <input
            ref={imageInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handleFileUpload}
          />
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.txt"
            style={{ display: 'none' }}
            onChange={handleFileUpload}
          />
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            style={{ display: 'none' }}
            onChange={handleFileUpload}
          />
        </div>
        {toast && (
          <div className="toast-message">
            {toast}
          </div>
        )}
      </div>

      {/* 인터넷/TV 가입신청 모달 */}
      {showApplyModal && (
        <InternetApplyModal
          roomId={roomId}
          onClose={() => setShowApplyModal(false)}
        />
      )}

      {/* 신청서 상세보기 모달 */}
      {showApplicationDetail && applicationDetail && (
        <div className="application-detail-overlay">
          <div className="application-detail-modal">
            <div className="application-detail-header">
              <h2>신청서 상세보기</h2>
              <button className="modal-close-btn" onClick={() => setShowApplicationDetail(false)}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M18 6L6 18M6 6L18 18" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
            <div className="application-detail-content">
              {/* 가입 내역 */}
              <div className="detail-section">
                <h3 className="detail-section-title">가입 내역</h3>
                <div className="detail-row">
                  <span className="detail-label">통신사</span>
                  <span className="detail-value">{applicationDetail.product_carrier || '-'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">상품구성</span>
                  <span className="detail-value">
                    {applicationDetail.internet_name && applicationDetail.tv_name
                      ? '인터넷 + TV'
                      : applicationDetail.internet_name
                        ? '인터넷'
                        : applicationDetail.tv_name
                          ? 'TV'
                          : '-'}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">요금제</span>
                  <span className="detail-value">
                    {[applicationDetail.internet_name, applicationDetail.tv_name].filter(Boolean).join(' + ') || '-'}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">월요금</span>
                  <span className="detail-value">
                    {applicationDetail.monthly_price
                      ? `${applicationDetail.monthly_price.toLocaleString()}원`
                      : '-'}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">사은품</span>
                  <span className="detail-value">
                    {applicationDetail.cash_gift
                      ? `${applicationDetail.cash_gift.toLocaleString()}원`
                      : '-'}
                  </span>
                </div>
              </div>

              {/* 개인정보 */}
              <div className="detail-section">
                <h3 className="detail-section-title">개인정보</h3>
                <div className="detail-row">
                  <span className="detail-label">이름</span>
                  <span className="detail-value">{applicationDetail.name || '-'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">주민번호</span>
                  <span className="detail-value">{applicationDetail.resident_number || '-'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">연락처</span>
                  <span className="detail-value">{applicationDetail.phone || '-'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">현재 통신사</span>
                  <span className="detail-value">{applicationDetail.current_carrier || '-'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">이메일</span>
                  <span className="detail-value">{applicationDetail.email || '-'}</span>
                </div>
              </div>

              {/* 설치정보 */}
              <div className="detail-section">
                <h3 className="detail-section-title">설치정보</h3>
                <div className="detail-row">
                  <span className="detail-label">설치주소</span>
                  <span className="detail-value">{applicationDetail.install_address || '-'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">상세주소</span>
                  <span className="detail-value">{applicationDetail.install_address_detail || '-'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">희망설치일</span>
                  <span className="detail-value">{applicationDetail.install_date || '-'}</span>
                </div>
              </div>

              {/* 결제정보 */}
              <div className="detail-section">
                <h3 className="detail-section-title">결제정보 (자동이체)</h3>
                <div className="detail-row">
                  <span className="detail-label">결제방법</span>
                  <span className="detail-value">{applicationDetail.payment_method === 'card' ? '카드결제' : '계좌이체'}</span>
                </div>
                {applicationDetail.payment_method === 'card' ? (
                  <>
                    <div className="detail-row">
                      <span className="detail-label">카드사</span>
                      <span className="detail-value">{applicationDetail.card_company || '-'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">카드주</span>
                      <span className="detail-value">{applicationDetail.card_holder || '-'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">카드번호</span>
                      <span className="detail-value">{applicationDetail.card_number || '-'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">유효기간</span>
                      <span className="detail-value">{applicationDetail.card_expiry || '-'}</span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="detail-row">
                      <span className="detail-label">은행명</span>
                      <span className="detail-value">{applicationDetail.payment_bank_name || '-'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">예금주</span>
                      <span className="detail-value">{applicationDetail.payment_account_holder || '-'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">계좌번호</span>
                      <span className="detail-value">{applicationDetail.payment_account_number || '-'}</span>
                    </div>
                  </>
                )}
              </div>

              {/* 입금정보 */}
              <div className="detail-section">
                <h3 className="detail-section-title">입금정보 (지원금수령용)</h3>
                <div className="detail-row">
                  <span className="detail-label">은행명</span>
                  <span className="detail-value">{applicationDetail.bank_name || '-'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">예금주</span>
                  <span className="detail-value">{applicationDetail.account_holder || '-'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">계좌번호</span>
                  <span className="detail-value">{applicationDetail.account_number || '-'}</span>
                </div>
              </div>

              {/* 신청정보 */}
              <div className="detail-section">
                <h3 className="detail-section-title">신청정보</h3>
                <div className="detail-row">
                  <span className="detail-label">신청일시</span>
                  <span className="detail-value">
                    {applicationDetail.created_at
                      ? new Date(applicationDetail.created_at).toLocaleString('ko-KR')
                      : '-'}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">상태</span>
                  <span className="detail-value">
                    {applicationDetail.status === 'pending' ? '접수완료' : applicationDetail.status}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 이미지 확대 모달 */}
      {selectedImage && (
        <div
          className="image-modal-overlay"
          onClick={() => setSelectedImage(null)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <img
            src={selectedImage}
            alt="확대 이미지"
            style={{
              maxWidth: '90%',
              maxHeight: '90%',
              objectFit: 'contain',
            }}
            onClick={(e) => e.stopPropagation()}
          />
          <button
            onClick={() => setSelectedImage(null)}
            style={{
              position: 'absolute',
              top: '20px',
              right: '20px',
              background: 'transparent',
              border: 'none',
              color: 'white',
              fontSize: '32px',
              cursor: 'pointer',
            }}
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
};

export default ChatDetail;
